import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import * as _ from 'lodash';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { Models } from '@ibexfusion/models'
import { DailyAnalyticLogsService } from '@services/daily-analytic-logs/daily-analytic-logs.service';
import { TranslateService } from '@services/translate/translate.service';
import { FilterConfigsService } from '@services/filter-configs/filter-configs.service';
import { MessengerService } from '@services/messenger/messenger.service';
import { PageAction } from '../../../components/page-actions/page-actions.component';
import { TitleSelectionComponent } from '../../../components/popUpSelects/title-selection/title-selection.component';
import { DailyAnalyticSessionLogsService } from '@services/daily-analytic-session-logs/daily-analytic-session-logs.service';

@Component({
    selector: 'app-analytics-detail',
    templateUrl: './analytics-detail.component.html',
    styleUrls: ['./analytics-detail.component.scss'],
    providers: [DailyAnalyticLogsService, DailyAnalyticSessionLogsService, MatPaginatorModule, FilterConfigsService, MessengerService],
    standalone: false
})

export class AnalyticsDetailComponent implements OnInit {
  public pageActions: PageAction[];
  public organisationName = '';
  public error = null;
  public fromDate = new Date(new Date(Date.now() - 1000 * 60 * 60 * 24 * 28).setHours(0, 0, 0, 0));
  public toDate = new Date();
  public maxDate = this.toDate;
  public types;
  public playOuts: { name: string, id: string, uuids: string[] }[];
  public activePlayOuts: { name: string, id: string, uuids: string[] }[];
  public noData = null;
  public noSessionData = null;
  public isLoaded = false;
  public totalCount = 0;
  public totalSessionCount = 0;
  public timelineChartData: any;
  public sessionTimelineChartData: any;
  public sessionByTimeTimelineChartData: any;
  public barChartData: any;
  public pieChartData: any;
  public defBarChartData: any;
  public barChartDataCount: any;
  public maxPage: any;
  public barChartTypes: any;
  public perPage = 10;
  public pageIndex = 0;
  public monthButtons;
  public typeFilter = 'all';
  public csvBarChartData: any;
  public savedFilters: Models.FilterConfig[];
  public playOutUuids: string[];
  private pathDepth = 0;
  private data;
  private sessionData;
  private initialData;
  private initialSessionData;
  private systemMessages;
  private selectedFilterName: string;
  availablePlayoutTypes = [];

  private unusedPopups = ['WidgetSubNavigation', 'FeedbackRequest', 'HomescreenRequest', 'VotingRequest', 'ThanksPopup', 'FinishVotingRequest', 'InitLogoutRequest'];

  constructor(
    private route: ActivatedRoute, private logService: DailyAnalyticLogsService, private ts: TranslateService, private router: Router, private filters: FilterConfigsService,
    private dialog: MatDialog, private sessionService: DailyAnalyticSessionLogsService
  ) {
  }

  async ngOnInit() {
    this.systemMessages = this.ts.get('system.analytics');
    this.monthButtons = Array.from({ length: 12 }, (_, i) => {
      const date = new Date();
      date.setDate(20);
      date.setMonth(date.getMonth() - i - 1);
      const y = date.getFullYear(), m = date.getMonth();

      return {
        title: date.toLocaleString('default', { month: 'short' }) + ' ' + y.toString().slice(-2),
        startDate: new Date(y, m, 1), endDate: new Date(y, m + 1, 0, 23, 59, 59)
      };
    });
    this.getSavedFilters();
    await this.getAnalytics();
    this.isLoaded = true;
    this.pageActions = [new PageAction({ icon: 'subdirectory_arrow_left', title: 'Go to Overview', callback: () => { this.router.navigate(['analytics']); } })];
  }

  getAnalyticsWithFilteredTypes() {
    return this.getAnalytics(false, true);
  }

  async getAnalytics(dateChanged = false, channelChanged = false) {
    this.reset();
    const tempToDate = _.clone(this.toDate);
    tempToDate.setDate(tempToDate.getDate() + 1);
    const query = {
      startDate: this.fromDate.toISOString(), endDate: tempToDate.toISOString(),
      $sort: { date: 1 }, customerId: this.route.snapshot.params['organisationCustomerId'] || this.route.snapshot.params['ibexCustomerId']
    };
    await Promise.all([this.getAnalyticsLogs(query, dateChanged, channelChanged), this.getAnalyticsSessions(query, dateChanged, channelChanged)]);
  }

  async getAnalyticsLogs(query, dateChanged = false, channelChanged = false) {
    if (!this.data || !!dateChanged || !!channelChanged) {
      await this.initQueryData(dateChanged);
      this.initialData = await this.loadBatchwise(this.logService, query);
      if (!this.initialData?.length) {
        this.noData = this.getSystemMessage('noData');
        this.isLoaded = true;
        return;
      }
    }
    this.data = this.combineLogsInPlace(this.filterData(this.correctPaths(this.initialData)));
    const tempData = _.cloneDeep(this.data);
    tempData.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    let data = {};
    if (tempData?.length) {
      const start = new Date(this.fromDate);
      const end = new Date(this.toDate);
      while (start <= end) {
        data[start.getDate() + '-' + start.getMonth() + '-' + start.getFullYear()] = 0;
        start.setDate(start.getDate() + 1);
      }
    }
    for (let i = 0; i < this.data.length; i++) {
      const date = new Date(this.data[i].date);
      data[date.getDate() + '-' + date.getMonth() + '-' + date.getFullYear()] = data[date.getDate() + '-' + date.getMonth() + '-' + date.getFullYear()] + this.data[i].total;
      this.totalCount = this.totalCount + this.data[i].total;
    }
    const newData = {};
    for (const key of Object.keys(data)) {
      const newKey = new Date(+key.split('-')[2], +key.split('-')[1], +key.split('-')[0]).getTime();
      newData[newKey] = data[key];
    }
    data = this.fixDataObject(newData);

    const keys = Object.keys(data);
    const arr = [];
    for (let i = 0; i < keys.length; i++) {
      const mom = moment(+keys[i]).add(6, 'h');
      if (mom.format('DDMMYY') === moment().format('DDMMYY')) {
        continue;
      }
      const formatDate = mom.format();
      arr.push([formatDate, data[keys[i]]]);
    }
    setTimeout(() => {
      this.barChartData = this.calculateBarChartData(this.data);
      if (this.playOuts && this.playOuts.length > 1) {
        this.pieChartData = this.calculatePieChartData(this.data);
      }
      this.timelineChartData = [{ name: 'Total', data: arr }];
      this.isLoaded = true;
    }, 100);
  }

  async getAnalyticsSessions(query, dateChanged = false, channelChanged = false) {
    if (!this.sessionData || !!dateChanged || !!channelChanged) {
      await this.initQueryData(dateChanged);
      this.initialSessionData = await this.loadBatchwise(this.sessionService, query);
      if (!this.initialSessionData?.length) {
        this.noSessionData = this.getSystemMessage('noData');
        this.isLoaded = true;
        return;
      }
    }
    this.sessionData = this.filterByType(this.filterData(this.initialSessionData));

    const tempData = _.cloneDeep(this.sessionData);
    tempData.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    let data = {};
    if (tempData?.length) {
      const start = new Date(this.fromDate);
      const end = new Date(this.toDate);
      while (start <= end) {
        data[start.getDate() + '-' + start.getMonth() + '-' + start.getFullYear()] = 0;
        start.setDate(start.getDate() + 1);
      }
    }

    for (const element of this.sessionData) {
      const date = new Date(element.date);
      data[date.getDate() + '-' + date.getMonth() + '-' + date.getFullYear()] = data[date.getDate() + '-' + date.getMonth() + '-' + date.getFullYear()] + element.logs.length;
      this.totalSessionCount = this.totalSessionCount + element.logs.length;
    }

    const newData = {};
    for (const key of Object.keys(data)) {
      const newKey = new Date(+key.split('-')[2], +key.split('-')[1], +key.split('-')[0]).getTime();
      newData[newKey] = data[key];
    }
    data = this.fixDataObject(newData);

    const keys = Object.keys(data);
    const arr = [];
    for (let i = 0; i < keys.length; i++) {
      const mom = moment(+keys[i]).add(6, 'h');
      if (mom.format('DDMMYY') === moment().format('DDMMYY')) {
        continue;
      }
      const formatDate = mom.format();
      arr.push([formatDate, data[keys[i]]]);
    }

    const arrByTime = this.countLogsByHour(this.sessionData);
    setTimeout(() => {
      this.sessionTimelineChartData = [{ name: 'Total', data: arr }];
      this.sessionByTimeTimelineChartData = { data: arrByTime.map(el => el.y), categories: arrByTime.map(el => el.x) };
    }, 100);
  }

  async loadBatchwise(service: any, query: any, batchSize = 5000): Promise<any[]> {
    const allResults: any[] = [];
    let skip = 0;
    let hasMore = true;
    while (hasMore) {
      try {
        const batch = await service.find(skip, batchSize, query);
        if (batch?.data?.length > 0) {
          allResults.push(...batch.data);
          skip += batchSize;
          if (batch.data.length < batchSize) {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      } catch (error) {
        throw error;
      }
    }
    return allResults;
  }

  countLogsByHour(dataArray) {
    const hourCounts = {};
    dataArray.forEach(data => {
      data.logs.forEach(log => {
        const timestamp = new Date(log.timestamp);
        const hour = timestamp.getUTCHours();
        hourCounts[hour] = !hourCounts[hour] ? 1 : hourCounts[hour] + 1
      });
    });
    const res = [];
    for (const key in hourCounts) {
      res.push({ x: key.length === 1 ? '0' + key + ':00' : key + ':00', y: hourCounts[key]});
    }
    return res;
  }

  fixDataObject(obj) {
    return Object.entries(obj).reduce((acc, [key, value]) => {acc[key] = typeof value === 'number' && isNaN(value) ? 0 : value;return acc;}, {});
  }

  reset() {
    for (const field of ['timelineChartData', 'sessionTimelineChartData', 'sessionByTimeTimelineChartData', 'barChartData', 'pieChartData', 'defBarChartData', 'csvBarChartData', 'noData', 'error']) {
      this[field] = null;
    }
    this.totalCount = 0;
    this.totalSessionCount = 0;
    this.isLoaded = false;
  }

  filterData(data) {
    let dataFiltered = _.filter(data, element => {
      return _.map(this.activePlayOuts, 'id').includes(element.id) || this.activePlayOuts.reduce((uuids, obj) => uuids.concat(obj.uuids), []).includes(element.id);
    });
    dataFiltered.forEach(item => {
      let index = item?.logs?.findIndex(log => !!log?.path?.endsWith('(overlay:screensaver)') || log?.path?.indexOf('helper:') > -1);
      while(index !== -1) {
        item.total = item.total - item.logs[index].count;
        item.logs.splice(index, 1);
        index = item?.logs?.findIndex(log => !!log?.path?.endsWith('(overlay:screensaver)') || log?.path?.indexOf('helper:') > -1);
      }
    });
    return this.anonymizeMessengerData(dataFiltered);
  }

  changePage(event?) {
    this.perPage = event?.pageSize || 10;
    this.pageIndex = event?.pageIndex || 0;
    this.barChartData = null;
    setTimeout(() => { this.barChartData = this.prepareData(this.defBarChartData.slice(this.pageIndex * this.perPage, ((this.pageIndex + 1) * this.perPage))); }, 10);
  }

  perPageChange(event) {
    this.perPage = event.value;
    this.pageIndex = 0;
    this.maxPage = Math.ceil(this.barChartDataCount / this.perPage) - 1;
    this.barChartData = null;
    setTimeout(() => { this.barChartData = this.prepareData(this.defBarChartData.slice(this.pageIndex * this.perPage, ((this.pageIndex + 1) * this.perPage))); }, 10);
  }

  onNavigate(direction) {
    const actions = {
      first: () => this.pageIndex = 0, last: () => this.pageIndex = Math.ceil(this.barChartDataCount / this.perPage) - 1,
      next: () => this.pageIndex++, previous: () => this.pageIndex--
    };
    actions[direction]?.();
    this.barChartData = null;
    setTimeout(() => {
      const start = this.pageIndex * this.perPage;
      const end = start + this.perPage;
      this.barChartData = this.prepareData(this.defBarChartData.slice(start, end));
    }, 10);
  }

  calculateBarChartData(data, slice = true) {
    const types = new Set();
    const result = {};
    data = this.anonymizeMessengerData(data);

    data.forEach(item => {
      item.logs.forEach(log => {
        types.add(log.type);
        let path = log.path;
        try {
          path = decodeURIComponent(path);
        } catch (err) {
          console.warn(log.path);
        }
        if (!result[path]) {
          result[path] = { total: 0 };
        }
        result[path].total += log.count;
        result[path][log.type] = (result[path][log.type] || 0) + log.count;
      });
    });

    this.barChartTypes = Array.from(types);
    const sortable = Object.keys(result).map(path => [path, result[path].total, result[path]]);
    sortable.sort((a, b) => b[1] - a[1]);
    this.defBarChartData = sortable;
    this.barChartDataCount = sortable.length;
    this.maxPage = Math.ceil(this.barChartDataCount / this.perPage) - 1;

    return this.prepareData(slice ? sortable.slice(0, 10) : sortable, Array.from(types));
  }

  combineLogsInPlace(dataArray) {
    dataArray.forEach(item => {
      if (!item.logs || !Array.isArray(item.logs)) return;
      const combinedMap = new Map();
      item.logs.forEach(log => {
        const cleanPath = log.path.replace(/#/g, '/');
        if (combinedMap.has(cleanPath)) {
          combinedMap.get(cleanPath).count += log.count;
        } else {
          log.path = cleanPath;
          combinedMap.set(cleanPath, log);
        }
      });
      item.logs = Array.from(combinedMap.values());
    });
    return dataArray;
  }

  anonymizeMessengerData(data) {
    for (let i = 0; i < data.length; i++) {
      if (data[i].logs.length) {
        const messengerLogs = [];
        for (let j = 0; j < data[i].logs.length; j++) {
          if (data[i]?.logs[j]?.path?.includes('/msg/')) {
            messengerLogs.push(data[i].logs[j]);
            data[i].logs.splice(j, 1);
            j--;
          }
        }
        if (messengerLogs.length) {
          const combined = {};

          messengerLogs.forEach(item => {
            const key = item.path.substring(0, 7);
            if (!combined[key]) {
              combined[key] = {...item, count: 0};
            }
            combined[key].count += item.count;
          });
          for (const key of Object.keys(combined)) {
            data[i].logs.push({ path: key, type: combined[key].type, count: combined[key].count, language: combined[key].language })
          }
        }
      }
    }
    return data;
  }

  calculatePieChartData(data) {
    this.pieChartData = null;
    const elementsById = _.groupBy(data, 'id');
    const res = [];
    let playoutTypes = [];
    for (const id of Object.keys(elementsById)) {
      let count = 0;
      let countByType = {};
      for (const element of elementsById[id]) {
        count += element.total;
        for (const log of element.logs) {
          countByType[log.type] = !!countByType[log.type] ? countByType[log.type] + log.count : log.count;
        }
      }
      if (!_.find(this.playOuts, { id })) {
        return null;
      }
      playoutTypes = playoutTypes.concat(Object.keys(countByType));
      res.push(Object.assign({}, { id, count, name: _.find(this.playOuts, { id }).name }, countByType));
    }
    this.availablePlayoutTypes = [...new Set(playoutTypes)];
    return _.orderBy(res, ['count'], ['desc']);
  }

  prepareData(sortable, types = this.barChartTypes) {
    const result = { series: [], categories: [] };
    for (const type of types) {
      result.series.push({ name: type, data: [] });
    }
    for (const sort of sortable) {
      result.categories.push(sort[0]);
      for (const type of types) {
        const index = result.series.findIndex(el => el.name === type);
        result.series[index].data.push(!!sort[2][type] ? sort[2][type] : 0);
      }
    }
    return result;
  }

  public changePathDepth(event) {
    this.pathDepth = event.value;
    this.barChartData = this.calculateBarChartData(this.getDeepenedData(event.value));
    this.changePage();
  }

  togglePlayout(event, playOut) {
    this.selectedFilterName = null;
    event.preventDefault();
    event.stopPropagation();
    if (_.findIndex(this.activePlayOuts, { id: playOut.id }) > -1) {
      this.activePlayOuts.splice(_.findIndex(this.activePlayOuts, { id: playOut.id }), 1);
    } else {
      this.activePlayOuts.push(playOut);
    }
    return this.getAnalytics(false, false);
  }

  isPlayoutActive(playOut) {
    return !!_.find(this.activePlayOuts, { id: playOut.id });
  }

  selectAllFilters() {
    this.selectedFilterName = null;
    this.activePlayOuts = _.clone(this.playOuts);
    return this.getAnalytics(false, false);
  }

  unselectAllFilters() {
    this.selectedFilterName = null;
    this.activePlayOuts = [];
  }

  async saveFilterConfiguration() {
    const dialogRef = this.dialog.open(TitleSelectionComponent, { width: '800px', data: { titleTranslation: 'analytics.filterTitle' } });

    const result = await dialogRef.afterClosed().toPromise();
    if (!result) {
      return;
    }
    const filter = new Models.FilterConfig();
    filter.populateFromJSON({
      title: result, config: { activePlayOuts: this.activePlayOuts },
      page: 'analytics/' + this.route.snapshot.params['organisationCustomerId'] || this.route.snapshot.params['ibexCustomerId']
    });
    await this.filters.create(filter);
    this.getSavedFilters();
  }

  deleteFilter(id) {
    this.filters.remove(id).then(() => { this.getSavedFilters(); });
  }

  applySavedFilter(filter: Models.FilterConfig) {
    this.selectedFilterName = filter.title;
    this.activePlayOuts = filter.config.activePlayOuts;
    return this.getAnalytics(false, false);
  }

  async getSavedFilters() {
    const res = await this.filters.find(0, 100, { page: 'analytics/' + this.route.snapshot.params['organisationCustomerId'] || this.route.snapshot.params['ibexCustomerId'], });
    this.savedFilters = res.data;
  }

  private formatDate(date) {
    const day = String(date.getDate()).padStart(2, '0'); // Get day with leading zero if needed
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Get month with leading zero if needed (Note: Month starts from 0)
    const year = date.getFullYear(); // Get full year

    return `${day}.${month}.${year}`;
  }

  downloadAnalyticsByPlayout(event: any) {
    let csvContent = '';
    event.stopPropagation();
    if (this.selectedFilterName) {
      csvContent += 'Aktiver Filter: ' + this.convertUmlauts(this.selectedFilterName) + '\r\n';
    }
    csvContent += 'Playout\t';
    this.availablePlayoutTypes.forEach(type => { csvContent += type + '\t'; });
    csvContent += 'Total\r\n';
    csvContent += this.pieChartData.map(el => {
      let row = el.name + '\t';
      this.availablePlayoutTypes.forEach(type => { row += (el[type] || '') + '\t'; });
      row += el.count;
      return row;
    }).join('\r\n');
    this.downloadCsv(csvContent);
  }

  downloadAnalytics(event: any) {
    event.stopPropagation();
    const data = this.calculateBarChartData(this.getDeepenedData(this.pathDepth), false);
    const formattedDateRange = `${this.formatDate(this.fromDate)} - ${this.formatDate(this.toDate)}`;
    const sortArray = ['ibexsmart', 'ibextouch', 'ibextablet', 'ibextv'];
    const totalResults = {};
    data.series.forEach(r => { totalResults[r.name] = r.data.reduce((acc, curr) => acc + curr, 0); });
    const header = ['path', ...data.series.sort((a, b) => sortArray.indexOf(a.name.toLowerCase()) - sortArray.indexOf(a.name.toLowerCase())).map(content => content.name), 'total',
      formattedDateRange];
    if (this.selectedFilterName) {
      header.push('Aktiver Filter: ' + this.selectedFilterName);
    }
    const res = [
      header, ['Total', ...data.series.sort((a, b) => sortArray.indexOf(a.name.toLowerCase()) - sortArray.indexOf(a.name.toLowerCase())).map(c => totalResults[c.name]),
        Object.values(totalResults).reduce((acc, curr) => parseInt(acc as any) + parseInt(curr as any), 0)],
      ...data.categories.map((category, i) => {
        const row = data.series.sort((a, b) => sortArray.indexOf(a.name.toLowerCase()) - sortArray.indexOf(a.name.toLowerCase())).map(content => content.data[i]);
        const total = row.reduce((acc, val) => acc + val, 0);
        return [category, ...row, total];
      })
    ];
    const csvContent = res.map(rowArray => {
      let row = rowArray.join('\t');
      try {
        row = decodeURIComponent(row);
      } catch (err) {
        console.warn('could not decode', rowArray.join('\t'));
      }
      return row;
    }).join('\r\n');
    this.downloadCsv(csvContent);
  }

  downloadCsv(csvContent) {
    const formattedDateRange = `${this.formatDate(this.fromDate)} - ${this.formatDate(this.toDate)}`;
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob([csvContent], { type: 'data:text/csv;charset=utf-8;' }));
    link.style.visibility = 'hidden';
    link.download = `Analytics Download ${formattedDateRange}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  getDeepenedData(depth, data = _.cloneDeep(this.data)) {
    return data.map(item => {
      const logs = item.logs.map(log => {
        const pathParts = log.path.split('/').splice(0, depth > 0 ? depth + 1 : 1000);
        log.path = pathParts.join('/');
        return log;
      });

      const groupedLogs = _.groupBy(logs, 'path');
      const newLogs = Object.entries(groupedLogs).reduce((acc, [path, logsByPath]) => {
        const types = _.groupBy(logsByPath, 'type');
        const logsArray = Object.entries(types).map(([type, logsByType]) => ({
          path, type, language: logsByType[0].language,
          count: logsByType.reduce((sum, log) => sum + log.count, 0)
        }));
        return acc.concat(logsArray);
      }, []);

      return { ...item, logs: newLogs };
    });
  }

  correctPaths(data) {
    data.forEach(item => {
      item.logs = item.logs.reduce((logs, log) => {
        try {
          log.path = decodeURIComponent(log.path.toLowerCase());
        } catch (err) {
          log.path = log.path.toLowerCase();
        }

        if (!/^\/\w\w\//.test(log.path) && log.path.length > 3 && log.language) {
          log.path = `/${log.language}/${log.path}`.replace('//', '/');
        }

        if (this.unusedPopups.some(unusedPath => log.path.includes(unusedPath))) {
          item.total -= log.count;
        } else if (
          log.path === '/' || log.path === '/not-found' || log.path === '[object Object]' || log.path.includes('myaccommodation') || log.path.length === 0 ||
          (this.typeFilter !== 'all' && this.typeFilter !== log.type)
        ) {
          item.total -= log.count;
        } else {
          logs.push(log);
        }

        return logs;
      }, []);
    });

    return data;
  }

  filterByType(elements) {
    if (this.typeFilter === 'all') {
      return elements;
    } else {
      return elements.map(data => {
        const filteredLogs = data.logs.filter(log => log.type === this.typeFilter);
        return { ...data, logs: filteredLogs };
      });
    }
  }

  getCountByPlayout(playOut) {
    let value = 0;
    const elements = _.filter(this.initialData, element => {
      return element.id === playOut.id || (playOut.uuids || []).includes(element.id);
    });
    for (const element of elements) {
      value += element.total;
    }
    return value;
  }

  setDateByButton(button: any) {
    this.isLoaded = false;
    this.fromDate = button.startDate;
    this.toDate = button.endDate;
    return this.getAnalytics(true);
  }

  private async initQueryData(dateChanged = false) {
    const tempToDate = _.clone(this.toDate);
    tempToDate.setDate(tempToDate.getDate() + 1);
    const query = {
      options: true, startDate: this.fromDate.toISOString(), endDate: tempToDate.toISOString(),
      customerId: this.route.snapshot.params['organisationCustomerId'] || this.route.snapshot.params['ibexCustomerId'],
    };
    const res = await this.logService.find(0, 1, query);
    const playOuts = [];
    for (const playOut of (res.data[0] as any).playouts) {
      playOuts.push({
        name: (playOut.title || playOut.name).replace(/^(Premium Touchscreen|ibextouch|ibexsmart|ibextablet)\s/i, ''),
        id: playOut.uuid || playOut._id, uuids: playOut.screenUuids || []
      });
    }
    this.playOuts = _.uniqBy(_.orderBy(playOuts, 'name'), 'id');
    this.playOutUuids = this.playOuts.reduce((uuids, obj) => uuids.concat(obj.uuids), []);
    if (!this.activePlayOuts) {
      this.activePlayOuts = _.clone(this.playOuts);
    }
    if (!this.types || dateChanged) {
      this.types = (res.data[0] as any).types;
      this.types.unshift('all');
    }
  }

  private getSystemMessage(message) {
    return this.systemMessages && this.systemMessages[message] ? this.systemMessages[message] : message;
  }

  private convertUmlauts(str) {
    const umlautMap = { 'Ä': 'Ae', 'Ö': 'Oe', 'Ü': 'Ue', 'ä': 'ae', 'ö': 'oe', 'ü': 'ue', 'ß': 'ss' };
    return str.replace(/[ÄÖÜäöüß]/g, match => umlautMap[match]);
  }
}
